import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FechasService } from '../../../services/utils/fechas.service';
import { ProjectService } from '../../../services/modules/project.service';
import { WorkShiftRotationService } from '../../../services/modules/work-shift-rotation.service';
import { CompaniesService } from '../../../services/modules/companies.service';
import { SnackBarService } from '../../../services/utils/snackBar.service';
import { DialogService } from '../../../services/utils/dialog.service';
import { WorkShiftConfiguration, WorkShiftSchedule } from '../../../interfaces/interfaces';
import { timeToMinutes, extractHHmm, formatHoursEs } from '../../../utils/time-format.util';
import { colorForTurnoIndex } from '../../../utils/work-shift-color.util';
import { AuthGuardService } from '../../../services/guards/AuthGuardService';
import {
  BIWEEKLY_CYCLE_DAYS,
  DAYS_PER_ROW,
  RotationSaveBody,
  WorkShiftRotation,
  describeRotationWarning,
  extractRotationWarnings
} from '../../../utils/work-shift-rotation.util';

export interface DialogData {
  title: string;
  CompanyInfoId: string;
  ProjectId: string;
  wShift: WorkShiftConfiguration;
}

export type WorkShiftTab = 'turnos' | 'patrones' | 'catorcenales';

/**
 * "Turno diario" — jornada única que aplica cualquier día de la semana (api/WorkShift/Daily).
 * startHour/endHour en null representa un turno de "Descanso" (día libre sin horario).
 */
interface TurnoTemplate {
  localId: string;
  id: string | null;
  name: string;
  isActive: boolean;
  startHour: string | null; // "HH:mm", null = descanso
  endHour: string | null;
  breakL: string; // "01"
  breakR: string; // "00"
  trdL: string;
  trdR: string;
  endsNextDay: boolean; // el backend aún no soporta turnos que cruzan medianoche
  color: string;
  startDate: string | null; // "YYYY-MM-DD"
  endDate: string | null;
}

/** Snapshot de un turno aplicado a un día concreto de un patrón (no referencia viva al turno). */
interface PatternDayAssignment {
  turnoName: string;
  turnoLocalId: string | null;
  startHour: string | null;
  endHour: string | null;
  breakTime: string; // "HH:mm"
  trd: string; // "HH:mm"
  endsNextDay: boolean;
  color: string;
}

/** Un patrón semanal = un WorkShiftConfiguration existente o uno nuevo aún sin guardar. */
interface PatternTab {
  localId: string;
  id: string | null;
  name: string;
  isActive: boolean;
  days: (PatternDayAssignment | null)[]; // longitud 7, indexado 0=Domingo..6=Sabado
  startDate: string | null; // "YYYY-MM-DD"
  endDate: string | null;
  isAssigned: boolean;
}

/** Un patrón catorcenal = una rotación del catálogo (api/WorkShiftRotation) de 14 días, o una
 * nueva aún sin guardar. A diferencia del patrón semanal, `days` va indexado por día del ciclo
 * (0…13), no por día de la semana. */
interface RotationTab {
  localId: string;
  id: string | null;
  name: string;
  isActive: boolean;
  days: (PatternDayAssignment | null)[]; // longitud 14, indexado por día del ciclo
  /** "Repetir indefinidamente" encendido = sin fecha fin (se envía endDate nulo). */
  repeatIndefinitely: boolean;
  /** Último día de vigencia ("YYYY-MM-DD") cuando el interruptor está apagado. */
  endDate: string | null;
  versionNumber: number;
  assignedContracts: number;
}

/** Lo que comparten un patrón semanal y una rotación para calcular las horas del ciclo. */
interface CycleDays {
  days: (PatternDayAssignment | null)[];
}

const DIAS_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DISPLAY_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Lunes..Sábado, Domingo

@Component({
  selector: 'app-dialog-work-shifts',
  templateUrl: './dialog-work-shifts.component.html',
  styleUrls: ['./dialog-work-shifts.component.scss']
})
export class DialogWorkShiftsComponent implements OnInit {
  readonly diasSemanaCortas = DIAS_CORTOS;
  readonly displayDayOrder = DISPLAY_DAY_ORDER;

  activeTab: WorkShiftTab = 'patrones';
  loading = true;
  saving = false;
  savingTurno = false;
  maxWeeklyWorkHours = 0;
  /** true en cuanto se guarda algo (turno o patrón) — determina si al cerrar se le avisa al
   * padre que debe refrescar su lista, ya que ahora se puede guardar varias veces sin cerrar. */
  private didSave = false;

  turnos: TurnoTemplate[] = [];
  /** El formulario "Crear/Editar turno" arranca colapsado — solo se muestra al pulsar "Crear
   * nuevo turno" o el lápiz de una tarjeta existente. */
  turnoFormExpanded = false;
  /** Turno cargado en el formulario para edición: se llena al abrir el diálogo apuntando a
   *  un  turno existente puntual, o al pulsar el lápiz de una tarjeta de "Turnos disponibles". */
  editingTurnoLocalId: string | null = null;
  /** Turno "armado" en la pestaña Patrones para asignarlo tocando un día (alternativa táctil al
   * arrastrar) — no tiene relación con el formulario de creación de turnos. */
  armedTurnoLocalId: string | null = null;
  turnoForm: FormGroup;
  turnoHoursInvalid = false;
  private turnoSeq = 0;

  patterns: PatternTab[] = [];
  /** El formulario "Crear/Editar patrón" arranca colapsado — solo se muestra al pulsar "Crear
   * nuevo patrón" o el lápiz de una tarjeta de "Patrones guardados". */
  patternFormExpanded = false;
  draftPattern: PatternTab;
  patternNameTouched = false;
  private patternSeq = 0;

  draggedTurnoId: string | null = null;
  dragOverDay: number | null = null;

  /** Ciclo catorcenal: los 14 días del ciclo, de a una semana (7) por fila. */
  readonly biweeklyCycleDays = BIWEEKLY_CYCLE_DAYS;
  readonly rotationWeeks: number[][] = [0, 1].map(week =>
    Array.from({ length: DAYS_PER_ROW }, (_, i) => week * DAYS_PER_ROW + i));

  rotations: RotationTab[] = [];
  /** El catálogo de rotaciones no se pudo leer (p. ej. backend sin el endpoint desplegado). */
  rotationsLoadError = false;
  rotationFormExpanded = false;
  draftRotation: RotationTab;
  rotationNameTouched = false;
  savingRotation = false;
  dragOverRotationDay: number | null = null;
  /** Estado "Activo" con el que se cargó la rotación en edición: si cambia, se manda el PATCH. */
  private draftRotationOriginalActive = true;
  private rotationSeq = 0;

  permissions: string[] = [];

  constructor(public dialogRef: MatDialogRef<DialogWorkShiftsComponent>,
              @Inject(MAT_DIALOG_DATA) public data: DialogData,
              private projectService: ProjectService,
              private rotationService: WorkShiftRotationService,
              private companiesService: CompaniesService,
              private snackBService: SnackBarService,
              private dialogService: DialogService,
              public fechasService: FechasService,
              private authService: AuthGuardService,
              private fb: FormBuilder) {
    this.permissions = this.authService.auth.getUser().claims;
    this.turnoForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/\S+/)]],
      isActive: [true],
      startHour: ['08:00'],
      endHour: ['17:00'],
      breakL: ['00', Validators.required],
      breakR: ['00', Validators.required],
      trdL: ['00', Validators.required],
      trdR: ['00', Validators.required]
    });
    this.draftPattern = this.blankPattern();
    this.draftRotation = this.blankRotation();
  }

  ngOnInit(): void {
    if (this.data.CompanyInfoId) {
      this.companiesService.getAdminConfiguration(this.data.CompanyInfoId).subscribe((config: any) => {
        this.maxWeeklyWorkHours = config?.MaxWorkedTimeByWeek ?? 0;
      });
    }

    this.loading = true;
    this.projectService.getWorkShift(this.data.ProjectId).subscribe({
      next: (list: WorkShiftConfiguration[]) => {
        const rawList = list || [];
        const dailyList = rawList.filter(ws => !!ws.Daily?.IsDaily);
        const patternList = rawList.filter(ws => !ws.Daily?.IsDaily);

        this.turnos = dailyList.map((ws, index) => this.mapWorkShiftToTurno(ws, index));
        this.patterns = patternList.map(ws => this.mapWorkShiftToPatternTab(ws));

        // Al crear (botón "+", sin wShift puntual) se abre en Turnos por defecto; sólo se
        // fuerza la pestaña Patrones cuando el dato apuntado es realmente un patrón existente.
        this.activeTab = 'turnos';
        if (this.data.wShift) {
          const patternMatch = this.patterns.find(p => p.id === this.data.wShift.Id);
          if (patternMatch) {
            this.loadPatternIntoDraft(patternMatch);
            this.activeTab = 'patrones';
          } else {
            const turnoMatch = this.turnos.find(t => t.id === this.data.wShift.Id);
            if (turnoMatch) {
              this.loadTurnoForEditing(turnoMatch.localId);
              this.activeTab = 'turnos';
            }
          }
        }

        this.loading = false;
        this.loadRotations();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  selectTab(tab: WorkShiftTab): void {
    this.activeTab = tab;
    // El turno "armado" para asignar tocando un día solo vale dentro de la pestaña donde se armó.
    this.armedTurnoLocalId = null;
  }

  // ---------- Turnos diarios ----------

  private mapWorkShiftToTurno(ws: WorkShiftConfiguration, index: number): TurnoTemplate {
    const daily = ws.Daily;
    const startHour = daily?.StartHour ? extractHHmm(daily.StartHour) : null;
    const endHour = daily?.EndHour ? extractHHmm(daily.EndHour) : null;
    const [breakL, breakR] = this.splitHHmm(daily?.BreakTime);
    const [trdL, trdR] = this.splitHHmm(daily?.WorkedTimeBeforeBreakTime);
    return {
      localId: ws.Id,
      id: ws.Id,
      name: ws.Name,
      isActive: ws.IsActive,
      startHour,
      endHour,
      breakL, breakR, trdL, trdR,
      endsNextDay: false,
      color: colorForTurnoIndex(index),
      startDate: this.toDateInputValue(ws.StartDate),
      endDate: this.toDateInputValue(ws.EndDate)
    };
  }

  private splitHHmm(value: string | null | undefined): [string, string] {
    if (!value) { return ['00', '00']; }
    const hhmm = extractHHmm(value);
    const [h, m] = hhmm.split(':');
    return [h || '00', m || '00'];
  }

  get armedTurno(): TurnoTemplate | undefined {
    return this.turnos.find(t => t.localId === this.armedTurnoLocalId);
  }

  get activeTurnos(): TurnoTemplate[] {
    return this.turnos.filter(t => this.isTurnoEffectivelyActive(t));
  }

  /** Un turno con "Vigente hasta" ya pasado se muestra y se trata como inactivo, aunque
   * IsActive siga en true en el backend — el 1 de septiembre con vigencia hasta el 31 de
   * agosto ya debe leerse como Inactivo. */
  isTurnoEffectivelyActive(turno: TurnoTemplate): boolean {
    if (!turno.isActive) { return false; }
    if (!turno.endDate) { return true; }
    return turno.endDate >= this.todayDateInputValue();
  }

  trackByTurnoId(_: number, turno: TurnoTemplate): string {
    return turno.localId;
  }

  /** Arma un turno en la pestaña Patrones para poder tocarlo y luego tocar un día (alternativa
   * táctil al arrastrar) — no toca el formulario de creación/edición de turnos. */
  armTurno(localId: string): void {
    this.armedTurnoLocalId = localId;
  }

  loadTurnoForEditing(localId: string): void {
    const turno = this.turnos.find(t => t.localId === localId);
    if (!turno) { return; }
    this.editingTurnoLocalId = localId;
    this.turnoHoursInvalid = false;
    this.turnoFormExpanded = true;
    this.turnoForm.patchValue({
      name: turno.name,
      isActive: turno.isActive,
      startHour: turno.startHour ?? '08:00',
      endHour: turno.endHour ?? '17:00',
      breakL: turno.breakL,
      breakR: turno.breakR,
      trdL: turno.trdL,
      trdR: turno.trdR
    });
  }

  /** "Crear nuevo turno": despliega el formulario en blanco. */
  openTurnoForm(): void {
    this.resetTurnoForm();
    this.turnoFormExpanded = true;
  }

  /** "Cancelar": descarta lo que se estuviera creando/editando y vuelve a colapsar el formulario. */
  cancelTurnoForm(): void {
    this.resetTurnoForm();
    this.turnoFormExpanded = false;
  }

  /** Vuelve el formulario a un turno en blanco, sin tocar la lista guardada. */
  resetTurnoForm(): void {
    this.editingTurnoLocalId = null;
    this.turnoHoursInvalid = false;
    this.turnoForm.reset({
      name: '',
      isActive: true,
      startHour: '08:00',
      endHour: '17:00',
      breakL: '00',
      breakR: '00',
      trdL: '00',
      trdR: '00'
    });
  }

  /** Activo → vigente desde hoy hasta "siempre" (31/12/3000). Inactivo → vigente sólo hoy, de
   * modo que a partir de mañana quede fuera de vigencia igual que si tuviera IsActive=false.
   * Se usa tanto para turnos como para patrones — ambos son WorkShift y comparten el mismo
   * mecanismo de vigencia (StartDate/EndDate). */
  private computeValidityDates(isActive: boolean): { startDate: string; endDate: string } {
    const today = this.todayDateInputValue();
    return { startDate: today, endDate: isActive ? '3000-12-31' : today };
  }

  /** Sólo recalcula StartDate/EndDate cuando el guardado implica una transición real de estado
   * (creación, o Activo↔Inactivo). Si se está reguardando algo ya existente sin tocar ese switch
   * (ej. repintar los días de un patrón que ya estaba Inactivo), se conserva la vigencia tal
   * como estaba — de lo contrario cada edición de horas empujaba el StartDate a "hoy", perdiendo
   * la fecha real desde la que aplicaba y rompiendo cualquier vista que derive el horario vigente
   * de un día pasado a partir de ese rango (StartDate/EndDate). */
  private resolveValidityDates(
    existing: { isActive: boolean; startDate: string | null; endDate: string | null } | null,
    isActive: boolean
  ): { startDate: string; endDate: string } {
    if (existing && existing.isActive === isActive && existing.startDate) {
      return {
        startDate: existing.startDate,
        endDate: existing.endDate ?? (isActive ? '3000-12-31' : this.todayDateInputValue())
      };
    }
    return this.computeValidityDates(isActive);
  }

  saveTurno(): void {
    const nameControl = this.turnoForm.get('name');
    const name = (nameControl.value ?? '').toString().trim();
    if (!name) {
      nameControl.setValue(null);
      nameControl.markAsTouched();
      return;
    }

    const v = this.turnoForm.getRawValue();

    if (!v.startHour || !v.endHour) {
      this.turnoHoursInvalid = true;
      return;
    }
    this.turnoHoursInvalid = false;

    const breakL = this.pad2(v.breakL);
    const breakR = this.pad2(v.breakR);
    const trdL = this.pad2(v.trdL);
    const trdR = this.pad2(v.trdR);

    const existing = this.turnos.find(t => t.localId === this.editingTurnoLocalId) ?? null;
    const isActive = !!v.isActive;
    const { startDate, endDate } = this.resolveValidityDates(existing, isActive);

    const payload: any = {
      name,
      isActive,
      companyInfoId: this.data.CompanyInfoId,
      projectId: this.data.ProjectId,
      startDate,
      endDate,
      startHour: `${v.startHour}:00`,
      endHour: `${v.endHour}:00`,
      breakTime: `${breakL}:${breakR}:00`,
      workedTimeBeforeBreakTime: `${trdL}:${trdR}:00`,
      leaveOnNextDay: false
    };

    this.savingTurno = true;
    const request = existing?.id
      ? this.projectService.updateWorkShiftDaily(existing.id, payload)
      : this.projectService.postWorkShiftDaily(payload);

    request.subscribe({
      next: (result: any) => {
        this.savingTurno = false;
        this.didSave = true;
        const id = result?.Id ?? existing?.id ?? null;
        const localId = id ?? existing?.localId ?? `new-${++this.turnoSeq}`;
        const saved: TurnoTemplate = {
          localId,
          id,
          name,
          isActive,
          startHour: v.startHour,
          endHour: v.endHour,
          breakL,
          breakR,
          trdL,
          trdR,
          endsNextDay: false,
          color: existing?.color ?? colorForTurnoIndex(this.turnos.length),
          startDate,
          endDate
        };
        const idx = this.turnos.findIndex(t => t.localId === this.editingTurnoLocalId);
        this.turnos = idx === -1 ? [...this.turnos, saved] : this.turnos.map((t, i) => (i === idx ? saved : t));
        this.resetTurnoForm();
        this.turnoFormExpanded = false;
        this.snackBService.openSuccess('Turno guardado correctamente');
      },
      error: (err: HttpErrorResponse) => {
        this.savingTurno = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  deleteTurno(turno: TurnoTemplate, event: Event): void {
    event.stopPropagation();
    if (!turno.id) { return; }

    const isAssigned = this.patterns.some(p => p.days.some(d => d && d.turnoLocalId === turno.localId));
    if (isAssigned) {
      this.dialogService.openDialogConfirm(
        'Acción no permitida',
        false, true, 'Cancelar', '',
        'El turno forma parte de un patrón activo. Modifica la asignación del patrón para poder borrarlo.'
      );
      return;
    }

    this.dialogService.openDialogConfirm(
      `¿Eliminar el turno "${turno.name}"? Esta acción no se puede deshacer.`,
      true, true, 'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.projectService.deleteWorkShift(turno.id).subscribe({
        next: () => {
          this.turnos = this.turnos.filter(t => t.localId !== turno.localId);
          if (this.editingTurnoLocalId === turno.localId) { this.resetTurnoForm(); }
          if (this.armedTurnoLocalId === turno.localId) { this.armedTurnoLocalId = null; }
          this.didSave = true;
          this.snackBService.openSuccess('Turno eliminado correctamente');
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.dialogService.openDialogConfirm(
              'Acción no permitida',
              false, true, 'Cancelar', '',
              err.error?.message || 'El turno forma parte de un patrón activo. Modifica la asignación del patrón para poder borrarlo.'
            );
          } else {
            this.snackBService.openError(this.extractErrorMessage(err));
          }
        }
      });
    });
  }

  padTurnoField(controlName: string): void {
    const control = this.turnoForm.get(controlName);
    if (!control) { return; }
    control.setValue(this.pad2(control.value));
  }

  private pad2(value: string): string {
    const digits = (value ?? '').toString().replace(/\D/g, '');
    return digits === '' ? '00' : digits.slice(0, 2).padStart(2, '0');
  }

  selectOnFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select());
  }

  /** Al completar los 2 dígitos de la mitad izquierda (horas) de un campo Descanso/Trd, salta
   * solo a la mitad derecha (minutos) — evita tener que hacer clic o Tab manualmente. */
  autoAdvance(event: Event, nextInput: HTMLInputElement): void {
    const value = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    if (value.length >= 2) {
      nextInput.focus();
      setTimeout(() => nextInput.select());
    }
  }

  turnoWorkedHoursLabel(turno: TurnoTemplate): string {
    if (turno.startHour == null || turno.endHour == null) { return 'Descanso'; }
    const workedMinutes = this.computeWorkedMinutes(
      turno.startHour, turno.endHour, turno.endsNextDay,
      `${turno.breakL}:${turno.breakR}`, `${turno.trdL}:${turno.trdR}`
    );
    return this.formatHHmm(workedMinutes);
  }

  turnoTotalHoursLabel(turno: TurnoTemplate): string {
    if (turno.startHour == null || turno.endHour == null) { return '—'; }
    const totalMinutes = this.computeTotalMinutes(turno.startHour, turno.endHour, turno.endsNextDay);
    return this.formatHHmm(totalMinutes);
  }

  /** "HH:mm" con ambos lados a dos dígitos, para que Efectivo/Total se vean con el mismo
   * formato que Tiempo de descanso/Trd en la tarjeta de "Turnos disponibles". */
  private formatHHmm(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  get turnoTotalTime(): string {
    const v = this.turnoForm.value;
    return this.formatDurationShort(this.computeTotalMinutes(v.startHour, v.endHour, false));
  }

  get turnoWorkHours(): string {
    const v = this.turnoForm.value;
    const minutes = this.computeWorkedMinutes(
      v.startHour, v.endHour, false, `${v.breakL}:${v.breakR}`, `${v.trdL}:${v.trdR}`
    );
    return this.formatDurationShort(minutes);
  }

  get turnoBreakSummary(): string {
    const v = this.turnoForm.value;
    const totalMinutes = this.mins(`${this.pad2(v.breakL)}:${this.pad2(v.breakR)}`);
    return this.formatDurationShort(totalMinutes);
  }

  private formatDurationShort(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) { return `${hours}h ${minutes}m`; }
    if (hours > 0) { return `${hours}h`; }
    return `${minutes}m`;
  }

  // ---------- Patrones semanales ----------

  private blankPattern(): PatternTab {
    return {
      localId: `new-${++this.patternSeq}`,
      id: null,
      name: '',
      isActive: true,
      days: new Array(7).fill(null),
      startDate: this.todayDateInputValue(),
      endDate: null,
      isAssigned: false
    };
  }

  private clonePatternForDraft(pattern: PatternTab): PatternTab {
    return { ...pattern, days: pattern.days.map(day => (day ? { ...day } : null)) };
  }

  /** Vuelve el draft a un patrón en blanco, sin insertarlo en la lista de guardados. */
  resetDraftPattern(): void {
    this.patternNameTouched = false;
    this.draftPattern = this.blankPattern();
  }

  loadPatternIntoDraft(pattern: PatternTab): void {
    this.patternNameTouched = false;
    this.draftPattern = this.clonePatternForDraft(pattern);
    this.patternFormExpanded = true;
  }

  /** "Crear nuevo patrón": despliega el formulario en blanco. */
  openPatternForm(): void {
    this.resetDraftPattern();
    this.patternFormExpanded = true;
  }

  /** "Cancelar": descarta lo que se estuviera creando/editando y vuelve a colapsar el formulario. */
  cancelPatternForm(): void {
    this.resetDraftPattern();
    this.patternFormExpanded = false;
  }

  private mapWorkShiftToPatternTab(ws: WorkShiftConfiguration): PatternTab {
    const days: (PatternDayAssignment | null)[] = new Array(7).fill(null);
    (ws.Schedule || []).forEach((schedule: WorkShiftSchedule) => {
      const startHour = extractHHmm(schedule.StartHour);
      const endHour = extractHHmm(schedule.EndHour);
      // Horarios antiguos (creados con el diálogo previo) guardan una fila por cada día de la
      // semana aunque no tenga horas — se trata igual que "sin turno asignado", no como un
      // turno de horas vacías.
      if (!startHour || !endHour) { return; }

      const matched = this.matchTurno(schedule);
      days[schedule.Day] = {
        turnoName: matched?.name ?? 'Horario propio',
        turnoLocalId: matched?.localId ?? null,
        startHour,
        endHour,
        breakTime: extractHHmm(schedule.BreakTime),
        trd: extractHHmm(schedule.WorkedTimeBeforeBreakTime),
        endsNextDay: schedule.EndDay != null && schedule.EndDay !== schedule.Day,
        color: matched?.color ?? '#64748B'
      };
    });
    return {
      localId: ws.Id,
      id: ws.Id,
      name: ws.Name,
      isActive: ws.IsActive,
      days,
      startDate: this.toDateInputValue(ws.StartDate),
      endDate: this.toDateInputValue(ws.EndDate),
      isAssigned: ws.IsAssigned ?? false
    };
  }

  private matchTurno(schedule: WorkShiftSchedule): TurnoTemplate | null {
    const startHour = extractHHmm(schedule.StartHour);
    const endHour = extractHHmm(schedule.EndHour);
    const breakTime = extractHHmm(schedule.BreakTime);
    const trd = extractHHmm(schedule.WorkedTimeBeforeBreakTime);
    return this.turnos.find(t =>
      t.startHour === startHour &&
      t.endHour === endHour &&
      `${t.breakL}:${t.breakR}` === breakTime &&
      `${t.trdL}:${t.trdR}` === trd
    ) ?? null;
  }

  trackByPatternId(_: number, pattern: PatternTab): string {
    return pattern.localId;
  }

  get patternNameInvalid(): boolean {
    return this.patternNameTouched && !(this.draftPattern.name ?? '').trim();
  }

  onTurnoDragStart(event: DragEvent, turnoLocalId: string): void {
    const turno = this.turnos.find(t => t.localId === turnoLocalId);
    if (!turno || !this.isTurnoEffectivelyActive(turno)) {
      event.preventDefault();
      return;
    }
    this.draggedTurnoId = turnoLocalId;
    event.dataTransfer?.setData('text/plain', turnoLocalId);
  }

  onTurnoDragEnd(): void {
    this.draggedTurnoId = null;
  }

  onDayDragOver(event: DragEvent, dayNumber: number): void {
    event.preventDefault();
    this.dragOverDay = dayNumber;
  }

  onDayDragLeave(): void {
    this.dragOverDay = null;
  }

  onDayDrop(event: DragEvent, dayNumber: number): void {
    event.preventDefault();
    this.dragOverDay = null;
    const turnoLocalId = this.draggedTurnoId || event.dataTransfer?.getData('text/plain');
    const turno = this.turnos.find(t => t.localId === turnoLocalId);
    this.draggedTurnoId = null;
    if (!turno || !this.isTurnoEffectivelyActive(turno)) { return; }
    this.assignTurnoToDay(this.draftPattern, dayNumber, turno);
  }

  onDayTap(dayNumber: number): void {
    if (!this.armedTurno || !this.isTurnoEffectivelyActive(this.armedTurno)) { return; }
    this.assignTurnoToDay(this.draftPattern, dayNumber, this.armedTurno);
  }

  removeDayAssignment(dayNumber: number, event: Event): void {
    event.stopPropagation();
    this.draftPattern.days[dayNumber] = null;
  }

  private assignTurnoToDay(pattern: PatternTab, dayNumber: number, turno: TurnoTemplate): void {
    pattern.days[dayNumber] = this.assignmentFromTurno(turno);
  }

  private assignmentFromTurno(turno: TurnoTemplate): PatternDayAssignment {
    return {
      turnoName: turno.name,
      turnoLocalId: turno.localId,
      startHour: turno.startHour,
      endHour: turno.endHour,
      breakTime: `${turno.breakL}:${turno.breakR}`,
      trd: `${turno.trdL}:${turno.trdR}`,
      endsNextDay: turno.endsNextDay,
      color: turno.color
    };
  }

  dayWorkedHours(assignment: PatternDayAssignment): number {
    if (assignment.startHour == null || assignment.endHour == null) { return 0; }
    return this.computeWorkedMinutes(
      assignment.startHour, assignment.endHour, assignment.endsNextDay, assignment.breakTime, assignment.trd
    ) / 60;
  }

  isRestAssignment(assignment: PatternDayAssignment): boolean {
    return assignment.startHour == null || assignment.endHour == null;
  }

  formatHoursEsLocal(hours: number): string {
    return formatHoursEs(this.roundToHalf(hours));
  }

  formatApiTimeRangeLocal(startHour: string, endHour: string): string {
    return `${startHour} – ${endHour}`;
  }

  patternTotalHours(pattern: CycleDays): number {
    return pattern.days.reduce((sum, day) => sum + (day ? this.dayWorkedHours(day) : 0), 0);
  }

  /** Suma de la jornada completa (sin descontar descanso) de cada día asignado. */
  patternTotalRawHours(pattern: CycleDays): number {
    return pattern.days.reduce((sum, day) => {
      if (!day || day.startHour == null || day.endHour == null) { return sum; }
      return sum + this.computeTotalMinutes(day.startHour, day.endHour, day.endsNextDay) / 60;
    }, 0);
  }

  patternBreakSummary(pattern: CycleDays): string {
    const totalMinutes = pattern.days.reduce((sum, day) => {
      if (!day || day.startHour == null || day.endHour == null) { return sum; }
      return sum + this.mins(day.breakTime);
    }, 0);
    return this.formatDurationShort(totalMinutes);
  }

  /** Días del ciclo con el turno "Descanso" asignado explícitamente — a diferencia de un día
   * vacío ("Suelta"), acá el usuario sí marcó el día como libre. Un turno Descanso no tiene
   * StartHour/EndHour, así que no aporta horas a Tiempo total/Horas efectivas (ya se excluye
   * correctamente de ambos); esto sólo cuenta cuántos días quedaron así marcados. */
  patternRestDaysCount(pattern: CycleDays): number {
    return pattern.days.reduce((count, day) => count + (day && this.isRestAssignment(day) ? 1 : 0), 0);
  }

  patternExceedsMax(pattern: PatternTab): boolean {
    if (!this.maxWeeklyWorkHours) { return false; }
    return this.patternTotalHours(pattern) > this.maxWeeklyWorkHours;
  }

  /** Días de la semana sin turno asignado: todos deben quedar programados (un día libre se
   * programa con el turno "Descanso"). */
  get patternUnscheduledDays(): string[] {
    return this.displayDayOrder
      .filter(dayNumber => !this.draftPattern.days[dayNumber])
      .map(dayNumber => this.diasSemanaCortas[dayNumber]);
  }

  get saveDisabled(): boolean {
    if (this.saving) { return true; }
    if (!(this.draftPattern.name ?? '').trim()) { return true; }
    if (this.patternUnscheduledDays.length > 0) { return true; }
    return this.patternExceedsMax(this.draftPattern);
  }

  save(): void {
    const name = (this.draftPattern.name ?? '').trim();
    if (!name) {
      this.patternNameTouched = true;
      return;
    }
    this.draftPattern.name = name;

    if (this.patternUnscheduledDays.length > 0) { return; }
    if (this.patternExceedsMax(this.draftPattern)) { return; }

    const schedule: any[] = [];
    for (let dayNumber = 0; dayNumber < 7; dayNumber++) {
      const assignment = this.draftPattern.days[dayNumber];
      if (!assignment) { continue; }
      schedule.push({
        Day: dayNumber,
        EndDay: assignment.endsNextDay ? (dayNumber + 1) % 7 : dayNumber,
        StartHour: assignment.startHour,
        EndHour: assignment.endHour,
        BreakTime: assignment.breakTime,
        WorkedTimeBeforeBreakTime: assignment.trd
      });
    }

    const existingPattern = this.patterns.find(p => p.localId === this.draftPattern.localId) ?? null;
    const { startDate, endDate } = this.resolveValidityDates(existingPattern, this.draftPattern.isActive);

    const dataSend = {
      Name: name,
      StartDate: startDate,
      EndDate: endDate,
      IsActive: this.draftPattern.isActive,
      Schedule: schedule,
      CompanyInfoId: this.data.CompanyInfoId,
      ProjectId: this.data.ProjectId
    };

    this.saving = true;
    const request = this.draftPattern.id
      ? this.projectService.updateWorkShift(this.draftPattern.id, dataSend)
      : this.projectService.postWorkShift(dataSend);

    request.subscribe({
      next: (result: any) => {
        this.saving = false;
        this.didSave = true;
        const savedId = result?.Id ?? this.draftPattern.id ?? null;
        // Si el backend devuelve el registro persistido (con Schedule), se refleja tal cual lo
        // guardó el servidor en vez de asumir que quedó igual al draft local — así una eventual
        // pérdida de datos del lado del servidor se nota de inmediato en la tarjeta, en vez de
        // recién al reabrir el diálogo.
        const savedPattern: PatternTab = Array.isArray(result?.Schedule)
          ? this.mapWorkShiftToPatternTab(result)
          : {
            ...this.draftPattern,
            id: savedId,
            localId: savedId ?? this.draftPattern.localId,
            startDate,
            endDate
          };
        const idx = this.patterns.findIndex(p => p.localId === this.draftPattern.localId);
        this.patterns = idx === -1 ? [...this.patterns, savedPattern] : this.patterns.map((p, i) => (i === idx ? savedPattern : p));
        this.resetDraftPattern();
        this.patternFormExpanded = false;
        this.snackBService.openSuccess('Patrón guardado correctamente');
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  deletePattern(pattern: PatternTab, event: Event): void {
    event.stopPropagation();
    if (!pattern.id) { return; }

    if (pattern.isAssigned) {
      this.dialogService.openDialogConfirm(
        'Acción no permitida',
        false, true, 'Cancelar', '',
        'El patrón forma parte de una asignación activa. Modifica la asignación para poder borrarlo.'
      );
      return;
    }

    this.dialogService.openDialogConfirm(
      `¿Eliminar el patrón "${pattern.name}"? Esta acción no se puede deshacer.`,
      true, true, 'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.projectService.deleteWorkShift(pattern.id).subscribe({
        next: () => {
          this.patterns = this.patterns.filter(p => p.localId !== pattern.localId);
          if (this.draftPattern.localId === pattern.localId) { this.resetDraftPattern(); }
          this.didSave = true;
          this.snackBService.openSuccess('Patrón eliminado correctamente');
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.dialogService.openDialogConfirm(
              'Acción no permitida',
              false, true, 'Cancelar', '',
              err.error?.message || 'El patrón forma parte de una asignación activa. Modifica la asignación para poder borrarlo.'
            );
          } else {
            this.snackBService.openError(this.extractErrorMessage(err));
          }
        }
      });
    });
  }

  // ---------- Patrones catorcenales (rotaciones de 14 días) ----------

  private loadRotations(): void {
    this.rotationService.getByProject(this.data.ProjectId).subscribe({
      next: (list: WorkShiftRotation[]) => {
        this.rotationsLoadError = false;
        // Esta pestaña solo maneja el ciclo catorcenal; rotaciones de otra longitud (el backend
        // admite 2…56 días) no se listan ni se editan aquí.
        this.rotations = list
          .filter(rotation => rotation.cycleLengthDays === BIWEEKLY_CYCLE_DAYS)
          .map(rotation => this.mapRotationToTab(rotation));
      },
      error: () => {
        this.rotationsLoadError = true;
      }
    });
  }

  private blankRotation(): RotationTab {
    return {
      localId: `new-rotation-${++this.rotationSeq}`,
      id: null,
      name: '',
      isActive: true,
      days: new Array(BIWEEKLY_CYCLE_DAYS).fill(null),
      repeatIndefinitely: true,
      endDate: null,
      versionNumber: 1,
      assignedContracts: 0
    };
  }

  /** Día libre del ciclo: reusa el turno "Descanso" del proyecto (mismo color) si existe. */
  private restAssignment(): PatternDayAssignment {
    const restTurno = this.turnos.find(t => t.startHour == null || t.endHour == null);
    return restTurno
      ? this.assignmentFromTurno(restTurno)
      : {
        turnoName: 'Descanso',
        turnoLocalId: null,
        startHour: null,
        endHour: null,
        breakTime: '00:00',
        trd: '00:00',
        endsNextDay: false,
        color: '#94A3B8'
      };
  }

  private mapRotationToTab(rotation: WorkShiftRotation): RotationTab {
    const days: (PatternDayAssignment | null)[] = new Array(BIWEEKLY_CYCLE_DAYS).fill(null);
    rotation.days.forEach(day => {
      if (day.dayIndex < 0 || day.dayIndex >= BIWEEKLY_CYCLE_DAYS) { return; }
      if (day.isRest || !day.workShiftId) {
        days[day.dayIndex] = this.restAssignment();
        return;
      }
      const turno = this.turnos.find(t => t.id === day.workShiftId);
      // Un turno que no es un turno diario del proyecto (p. ej. un patrón semanal, que el backend
      // permite en ciclos múltiplos de 7) se pinta solo con su nombre y sin horas, pero conserva
      // su WorkShiftId para no alterarlo al volver a guardar.
      days[day.dayIndex] = turno
        ? this.assignmentFromTurno(turno)
        : {
          turnoName: day.workShiftName ?? 'Turno',
          turnoLocalId: day.workShiftId,
          startHour: '00:00',
          endHour: '00:00',
          breakTime: '00:00',
          trd: '00:00',
          endsNextDay: false,
          color: '#64748B'
        };
    });
    return {
      localId: rotation.id,
      id: rotation.id,
      name: rotation.name,
      isActive: rotation.isActive,
      days,
      repeatIndefinitely: rotation.repeatIndefinitely,
      endDate: rotation.endDate,
      versionNumber: rotation.versionNumber,
      assignedContracts: rotation.assignedContracts
    };
  }

  trackByRotationId(_: number, rotation: RotationTab): string {
    return rotation.localId;
  }

  /** Etiqueta de la columna: el ciclo catorcenal son dos semanas Lun…Dom (día 0 = lunes). */
  rotationDayLabel(dayIndex: number): string {
    return DIAS_CORTOS[DISPLAY_DAY_ORDER[dayIndex % DAYS_PER_ROW]];
  }

  openRotationForm(): void {
    this.resetDraftRotation();
    this.rotationFormExpanded = true;
  }

  cancelRotationForm(): void {
    this.resetDraftRotation();
    this.rotationFormExpanded = false;
  }

  private resetDraftRotation(): void {
    this.rotationNameTouched = false;
    this.draftRotationOriginalActive = true;
    this.draftRotation = this.blankRotation();
  }

  loadRotationIntoDraft(rotation: RotationTab): void {
    this.rotationNameTouched = false;
    this.draftRotationOriginalActive = rotation.isActive;
    this.draftRotation = { ...rotation, days: rotation.days.map(day => (day ? { ...day } : null)) };
    this.rotationFormExpanded = true;
  }

  get rotationNameInvalid(): boolean {
    return this.rotationNameTouched && !(this.draftRotation.name ?? '').trim();
  }

  /** Tope de horas efectivas del ciclo: el doble de las horas semanales máximas configuradas
   * (dos semanas), p. ej. 84 h con un máximo de 42 h/semana. 0 = sin tope configurado. */
  get maxRotationWorkHours(): number {
    return this.maxWeeklyWorkHours * (BIWEEKLY_CYCLE_DAYS / DAYS_PER_ROW);
  }

  rotationExceedsMax(rotation: CycleDays): boolean {
    if (!this.maxRotationWorkHours) { return false; }
    return this.patternTotalHours(rotation) > this.maxRotationWorkHours;
  }

  /** Días del ciclo con una jornada real (turno con horario): el backend exige al menos uno. */
  rotationWorkDaysCount(rotation: CycleDays): number {
    return rotation.days.reduce((count, day) => count + (day && !this.isRestAssignment(day) ? 1 : 0), 0);
  }

  get rotationHasNoWorkDays(): boolean {
    return this.rotationWorkDaysCount(this.draftRotation) === 0;
  }

  /** Para no mostrar el aviso "al menos un día de trabajo" con el formulario recién abierto y vacío. */
  get rotationHasAnyAssignment(): boolean {
    return this.draftRotation.days.some(day => !!day);
  }

  /** Con "Repetir indefinidamente" apagado la fecha fin es obligatoria y no puede ser pasada. */
  get rotationEndDateInvalid(): boolean {
    if (this.draftRotation.repeatIndefinitely) { return false; }
    return !this.draftRotation.endDate || this.draftRotation.endDate < this.todayDateInputValue();
  }

  get todayDateKey(): string {
    return this.todayDateInputValue();
  }

  /** Días del ciclo sin turno asignado ("Lun S1" … "Dom S2"): todos deben quedar programados
   * (un día libre se programa con el turno "Descanso"). */
  get rotationUnscheduledDays(): string[] {
    return this.draftRotation.days.reduce<string[]>((missing, day, dayIndex) => {
      if (!day) { missing.push(`${this.rotationDayLabel(dayIndex)} S${Math.floor(dayIndex / DAYS_PER_ROW) + 1}`); }
      return missing;
    }, []);
  }

  get rotationSaveDisabled(): boolean {
    if (this.savingRotation) { return true; }
    if (!(this.draftRotation.name ?? '').trim()) { return true; }
    if (this.rotationUnscheduledDays.length > 0) { return true; }
    if (this.rotationEndDateInvalid) { return true; }
    if (this.rotationHasNoWorkDays) { return true; }
    return this.rotationExceedsMax(this.draftRotation);
  }

  onRotationDayDragOver(event: DragEvent, dayIndex: number): void {
    event.preventDefault();
    this.dragOverRotationDay = dayIndex;
  }

  onRotationDayDragLeave(): void {
    this.dragOverRotationDay = null;
  }

  onRotationDayDrop(event: DragEvent, dayIndex: number): void {
    event.preventDefault();
    this.dragOverRotationDay = null;
    const turnoLocalId = this.draggedTurnoId || event.dataTransfer?.getData('text/plain');
    const turno = this.turnos.find(t => t.localId === turnoLocalId);
    this.draggedTurnoId = null;
    if (!turno || !this.isTurnoEffectivelyActive(turno)) { return; }
    this.draftRotation.days[dayIndex] = this.assignmentFromTurno(turno);
  }

  onRotationDayTap(dayIndex: number): void {
    if (!this.armedTurno || !this.isTurnoEffectivelyActive(this.armedTurno)) { return; }
    this.draftRotation.days[dayIndex] = this.assignmentFromTurno(this.armedTurno);
  }

  removeRotationDay(dayIndex: number, event: Event): void {
    event.stopPropagation();
    this.draftRotation.days[dayIndex] = null;
  }

  saveRotation(): void {
    const name = (this.draftRotation.name ?? '').trim();
    if (!name) {
      this.rotationNameTouched = true;
      return;
    }
    this.draftRotation.name = name;
    if (this.rotationSaveDisabled) { return; }

    // Cada día del ciclo es un turno o un descanso, nunca nulo: un día sin asignar ("Suelta") o con
    // el turno "Descanso" se manda como descanso.
    const body: RotationSaveBody = {
      name,
      companyInfoId: this.data.CompanyInfoId,
      projectId: this.data.ProjectId,
      cycleLengthDays: BIWEEKLY_CYCLE_DAYS,
      // Encendido: sin fecha fin (null). Apagado: la fecha elegida en "Vigente hasta".
      endDate: this.draftRotation.repeatIndefinitely ? null : this.draftRotation.endDate,
      days: this.draftRotation.days.map((assignment, dayIndex) =>
        !assignment || this.isRestAssignment(assignment) || !assignment.turnoLocalId
          ? { dayIndex, workShiftId: null, isRest: true }
          : { dayIndex, workShiftId: assignment.turnoLocalId, isRest: false })
    };

    const editingId = this.draftRotation.id;
    const wantActive = this.draftRotation.isActive;
    const assignedContracts = this.draftRotation.assignedContracts;
    // El body del catálogo no trae IsActive: el estado se cambia aparte (PATCH {id}/Active).
    const activeChanged = editingId ? wantActive !== this.draftRotationOriginalActive : wantActive === false;

    this.savingRotation = true;
    const request$ = editingId
      ? this.rotationService.update(editingId, body)
      : this.rotationService.create(body);

    request$.pipe(
      switchMap((result: any) => {
        const savedId = result?.Id ?? editingId;
        if (savedId && activeChanged) {
          return this.rotationService.setActive(savedId, wantActive).pipe(map(() => result));
        }
        return of(result);
      })
    ).subscribe({
      next: (result: any) => {
        this.savingRotation = false;
        this.didSave = true;
        const warnings = extractRotationWarnings(result).map(w => describeRotationWarning(w, assignedContracts));
        const versionNote = result?.VersionCreated ? ' Se creó una nueva versión del patrón.' : '';
        this.cancelRotationForm();
        this.loadRotations();
        if (warnings.length) {
          this.snackBService.openWarning(`Patrón guardado.${versionNote} ${warnings.join(' ')}`.trim());
        } else {
          this.snackBService.openSuccess(`Patrón guardado correctamente.${versionNote}`.trim());
        }
      },
      error: (err: HttpErrorResponse) => {
        this.savingRotation = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  close(): void {
    this.dialogRef.close(this.didSave);
  }

  /** Cubre los 3 formatos de respuesta del backend: 409 negocio (camelCase message), resto (Error PascalCase). */
  private extractErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 409 && err.error?.message) { return err.error.message; }
    return err.error?.Error || err.error?.Message || 'Ocurrió un error al procesar el horario de trabajo';
  }

  // ---------- Utilidades de fecha ----------

  private toDateInputValue(value: string | null | undefined): string | null {
    if (!value) { return null; }
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }

  private todayDateInputValue(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // ---------- Cálculo de horas (compartido turno/patrón) ----------

  private mins(time: string): number {
    return timeToMinutes(time) ?? 0;
  }

  private computeTotalMinutes(start: string, end: string, endsNextDay: boolean): number {
    let diff = this.mins(end) - this.mins(start);
    if (endsNextDay) {
      diff += 24 * 60;
    } else if (diff < 0) {
      diff += 24 * 60;
    }
    return Math.max(0, diff);
  }

  private computeWorkedMinutes(start: string, end: string, endsNextDay: boolean, breakTime: string, trd: string): number {
    const total = this.computeTotalMinutes(start, end, endsNextDay);
    const breakMinutes = this.mins(breakTime);
    const trdMinutes = this.mins(trd);
    return total > trdMinutes ? Math.max(0, total - breakMinutes) : total;
  }

  private roundToHalf(value: number): number {
    return Math.round(value * 2) / 2;
  }

  exitPermission(permiso: string): boolean {
    let exit = false;
    const permisionsUserInfo = this.permissions.find(x => x === permiso);
    permisionsUserInfo !== undefined ? exit = true : exit = false;
    return exit;
  }
}
